import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactPaginate from "react-paginate";
import { postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import ViewInquiryModal from "../../../Modal/ViewInquiryModal";
import AssignmentHistoryModal from "../../../Modal/AssignmentHistoryModal";
import WhatsAppModal from "../../../Modal/WhatsAppModal";

const UserFollowupLeads = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const passedUserName = location.state?.userName;

  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [currentInquiryHistory, setCurrentInquiryHistory] = useState([]);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const rowsPerPage = 10;

  const fetchInquiries = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await postData("api/lead-work-history/all", {
        limit: rowsPerPage,
        page: currentPage + 1,
        search: searchTerm.trim(),
        startDate,
        endDate,
        leadType: "homepage",
        status:
          statusFilter && statusFilter !== "ALL" ? statusFilter : undefined,
        userId,
      });

      if (response?.success) {
        const data = response.data || {};
        let docs = data.docs || [];
        docs = docs.filter((d) => d?.user?._id === userId);
        setInquiries(docs);
        setTotalInquiries(data.total || docs.length || 0);
        setTotalPages(data.pages || 1);
      } else {
        toast.error(response?.message || "Failed to fetch follow-up leads");
      }
    } catch (error) {
      toast.error("Error fetching follow-up leads");
      console.error("Fetch follow-up leads error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "NEW":
        return "New Lead";
      case "ASSIGNED":
        return "Assigned";
      case "IN_PROGRESS":
        return "In Progress";
      case "FOLLOW_UP":
        return "Follow Up";
      case "CLOSED":
        return "Closed";
      case "NOT_INTERESTED":
        return "Not Interested";
      default:
        return status || "N/A";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getAssignedDate = (inquiry) => {
    const lead = inquiry.lead || {};
    const history = lead.assignHistory || [];

    if (Array.isArray(history) && history.length > 0) {
      const latest = history[history.length - 1];
      return formatDate(latest.assignedAt);
    }

    return formatDate(inquiry.createdAt);
  };

  useEffect(() => {
    fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, startDate, endDate, statusFilter, userId]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchInquiries();
  };

  const handleReset = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("ALL");
    setCurrentPage(0);
  };

  const handleView = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowViewModal(true);
  };

  const handleShowHistory = (leadId) => {
    if (!leadId) return;

    const historyForLead = inquiries
      .filter((item) => item?.lead?._id === leadId)
      .map((item) => ({
        ...item,
        assignedTo: item.user,
        assignedToModel: item.userModel || "User",
        assignedAt: item.createdAt,
      }));

    setCurrentInquiryHistory(historyForLead || []);
    setShowHistoryModal(true);
  };

  const handleWhatsAppClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowWhatsAppModal(true);
  };

  const headerTitle = passedUserName
    ? `Followup Leads - ${passedUserName}`
    : "Followup Leads - Agent";

  return (
    <>
      <div className="content-page">
        <ToastContainer position="top-right" autoClose={3000} />
        <CustomLoader loading={loading} />
        <div className="content">
          <div className="container-fluid">
            <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
              <div className="flex-grow-1">
                <h4 className="fs-18 fw-semibold m-0">{headerTitle}</h4>
                <p className="text-muted mb-0">
                  Total Assigned Leads: {totalInquiries}
                </p>
              </div>
              <div className="mt-2 mt-sm-0">
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={() => navigate(-1)}
                >
                  <i className="mdi mdi-arrow-left me-1"></i>
                  Back
                </button>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      <div className="row g-3 align-items-end">
                        <div className="col-md-12 col-lg-3">
                          <label htmlFor="Search" className="form-label">
                            Search
                          </label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control ps-4"
                              placeholder="Search by name, email or mobile..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <span className="input-group-text">
                              <i className="mdi mdi-magnify"></i>
                            </span>
                          </div>
                        </div>

                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="statusFilter" className="form-label">
                            Status
                          </label>
                          <select
                            id="statusFilter"
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => {
                              setStatusFilter(e.target.value);
                              setCurrentPage(0);
                            }}
                          >
                            <option value="ALL">All</option>
                            <option value="NEW">New Lead</option>
                            <option value="ASSIGNED">Assigned</option>
                            <option value="IN_PROGRESS">In Progress</option>
                            <option value="FOLLOW_UP">Follow Up</option>
                            <option value="CLOSED">Closed</option>
                            <option value="NOT_INTERESTED">
                              Not Interested
                            </option>
                          </select>
                        </div>

                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="startDate" className="form-label">
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>

                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="endDate" className="form-label">
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                          />
                        </div>

                        <div className="col-md-12 col-lg-3 d-flex gap-2 align-items-end">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            Search
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleReset}
                            disabled={loading}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-2">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    {loading ? (
                      <div className="text-center py-5">
                        <CustomLoader />
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover table-centered mb-0">
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>Lead Name</th>
                              <th>Agent</th>
                              <th>Assign Count</th>
                              <th>Status</th>
                              <th>Note</th>
                              <th>Assigned Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inquiries?.length > 0 ? (
                              inquiries.map((inquiry, index) => {
                                const lead = inquiry.lead || {};
                                const agent = inquiry.user || {};
                                return (
                                  <tr key={inquiry._id}>
                                    <td>
                                      {currentPage * rowsPerPage + index + 1}
                                    </td>
                                    <td>{lead.fullName || "N/A"}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 text-primary text-decoration-underline"
                                        onClick={() =>
                                          handleShowHistory(lead._id)
                                        }
                                      >
                                        {agent.fullName || "N/A"}
                                      </button>
                                    </td>
                                    <td>
                                      {Array.isArray(lead.assignHistory)
                                        ? lead.assignHistory.length
                                        : 0}
                                    </td>
                                    <td>{getStatusLabel(inquiry.status)}</td>
                                    <td>{inquiry.note || "-"}</td>
                                    <td>{getAssignedDate(inquiry)}</td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <button
                                          className="btn btn-sm btn-primary"
                                          onClick={() => handleView(lead)}
                                        >
                                          <i className="mdi mdi-eye"></i> View
                                        </button>
                                        <button
                                          className="btn btn-sm btn-success"
                                          onClick={() =>
                                            handleWhatsAppClick(lead)
                                          }
                                        >
                                          <i className="mdi mdi-whatsapp"></i>{" "}
                                          WhatsApp
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center py-4">
                                  No follow-up leads found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {totalPages > 1 && (
                          <ReactPaginate
                            previousLabel={
                              <i className="mdi mdi-chevron-left"></i>
                            }
                            nextLabel={
                              <i className="mdi mdi-chevron-right"></i>
                            }
                            breakLabel="..."
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            pageCount={totalPages}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageChange}
                            containerClassName="pagination pagination-rounded justify-content-end mt-3"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            activeClassName="active"
                            forcePage={currentPage}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ViewInquiryModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        inquiry={selectedInquiry}
      />

      <AssignmentHistoryModal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        history={currentInquiryHistory}
      />

      <WhatsAppModal
        show={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        inquiry={selectedInquiry}
      />
    </>
  );
};

export default UserFollowupLeads;
